import UserFriendlyExpressionBuilder from 'app/services/formula/parser/userFriendlyExpressionBuilder/userFriendlyExpressionBuilder';
import _ from 'lodash';

import { normalizeAstKeys } from 'utils/expressions';
import { jsonStringify, parseJSONObject } from 'utils/json';

import { GENERATE_ROOT_TYPE_NAME } from './GenerateExpression.constants';

/**
 * Converts an AST (`ExpressionJson` returned by the BE
 * `propose_final` tool call) back into the user-friendly textual form
 * the Monaco expression editor stores. Returns `null` when the AST is
 * malformed or the builder hits an unsupported shape so the modal can
 * surface a clear "preview unavailable" state instead of writing
 * garbage into the form field.
 */
export const astToUserFriendlyText = (
  expressionJson,
  { isNewExpressionsParsingLogicEnabled } = {},
) => {
  if (_.isEmpty(expressionJson)) {
    return null;
  }

  try {
    const parsed = parseJSONObject(expressionJson);

    if (_.isEmpty(parsed)) {
      return null;
    }

    const normalized = normalizeAstKeys(parsed);
    const text = UserFriendlyExpressionBuilder.build(normalized, false, {
      isNewExpressionsParsingLogicEnabled,
    });

    return _.isEmpty(text) ? null : text;
  } catch {
    return null;
  }
};

/**
 * Serialises the section's cached schema (an `ExpressionDefinitionDto`)
 * into the JSON string the Lender.Optimus.Ai
 * `POST /ai-expressions/generate` handler accepts in the `schemaJson`
 * payload field. When supplied the handler skips its server-side
 * schema fetch and slices this inline payload directly. Returns
 * `null` when the schema isn't safely serialisable so the handler
 * falls back to the domain-based path.
 */
export const serializeSchemaForGenerate = schemaParams => {
  if (_.isNil(schemaParams) || !_.isObject(schemaParams)) {
    return null;
  }

  try {
    return jsonStringify(schemaParams);
  } catch {
    return null;
  }
};

/**
 * Picks the `ExpectedRootTypeName` the BE `validate_expression` /
 * `propose_final` tool calls validate against.
 *
 * <ul>
 *   <li>Predicate fields (`isOnlyBooleanAllowed`) must evaluate to
 *       <code>bool</code>.</li>
 *   <li>Everything else uses <code>string</code> — the validator's
 *       "expected type is string → always compatible" passthrough
 *       (see <code>ExpressionValidator.IsExpressionCompatibleWithType</code>)
 *       lets calc-field decimals, ints, dates, etc. validate
 *       without us having to know the concrete return type at the
 *       call site.</li>
 * </ul>
 */
export const resolveExpectedRootTypeName = ({ isOnlyBooleanAllowed }) =>
  isOnlyBooleanAllowed ? GENERATE_ROOT_TYPE_NAME.BOOL : GENERATE_ROOT_TYPE_NAME.STRING;

export const isCancelledError = error =>
  error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';

/**
 * Normalises the `pendingQuestion.options` payload into the
 * `{ value, label, description }` shape the FE renders. The BE may
 * emit either the structured object shape (PR #283 contract) or a
 * legacy plain-string array (older runs / models that fall back to
 * the simpler shape). Plain strings are wrapped as
 * `{ value: s, label: null, description: null }` so the card can
 * still render the field path as its only affordance. Returns an
 * empty array when there are no usable options so the caller can
 * render only the free-text answer field.
 */
export const normalizeClarifyOptions = options => {
  if (_.isEmpty(options)) {
    return [];
  }

  return _.compact(
    options.map(option => {
      if (_.isString(option)) {
        const trimmed = _.trim(option);

        return _.isEmpty(trimmed) ? null : { value: trimmed, label: null, description: null };
      }

      if (!_.isPlainObject(option)) {
        return null;
      }

      const value = _.trim(option.value);

      return _.isEmpty(value)
        ? null
        : {
            value,
            label: _.isEmpty(_.trim(option.label)) ? null : _.trim(option.label),
            description: _.isEmpty(_.trim(option.description)) ? null : _.trim(option.description),
          };
    }),
  );
};

/**
 * Picks the index of the option the FE pre-highlights as the
 * "recommended" pick. Mirrors the BE-side validation in
 * `GenerateExpressionQueryHandler.ParseDefaultOptionIndex` — out-of-range
 * and non-integer inputs are dropped so we never crash on garbage.
 */
export const resolveDefaultClarifyOptionIndex = (defaultOptionIndex, options) => {
  if (_.isEmpty(options) || !_.isInteger(defaultOptionIndex)) {
    return null;
  }

  return defaultOptionIndex >= 0 && defaultOptionIndex < options.length ? defaultOptionIndex : null;
};
